import { useState, useEffect } from "react";
import { Image, Skeleton, Box } from "@mantine/core";
import { getFileUrl } from "@/shared/utils/useFileUrl";

interface S3ImageProps {
  fileId: string;
  alt?: string;
  radius?: string;
  fit?: "contain" | "cover" | "fill" | "none" | "scale-down";
  style?: React.CSSProperties;
  fallback?: React.ReactNode;
  onClick?: () => void;
}

/**
 * Component that loads images from S3 using presigned URLs.
 * Shows a skeleton loader while fetching the URL.
 */
export default function S3Image({
  fileId,
  alt = "Image",
  radius = "md",
  fit = "cover",
  style,
  fallback,
  onClick,
}: S3ImageProps) {
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let mounted = true;

    const fetchUrl = async () => {
      if (!fileId) {
        setLoading(false);
        return;
      }

      try {
        const presignedUrl = await getFileUrl(fileId);
        if (mounted) {
          setUrl(presignedUrl);
          setLoading(false);
        }
      } catch (err) {
        console.error("Failed to fetch S3 image URL:", err);
        if (mounted) {
          setError(true);
          setLoading(false);
        }
      }
    };

    fetchUrl();

    return () => {
      mounted = false;
    };
  }, [fileId]);

  if (loading) {
    return <Skeleton style={style} radius={radius} />;
  }

  if (error || !url) {
    return fallback ? <>{fallback}</> : <Box style={style} bg="gray.1" />;
  }

  return (
    <Image
      src={url}
      alt={alt}
      radius={radius as any}
      fit={fit}
      style={{ cursor: onClick ? "pointer" : undefined, ...style }}
      onClick={onClick}
      onError={() => setError(true)}
    />
  );
}
